import React, { useState } from 'react';
import { Card, Button, Table, App, Input, Space, InputNumber, Row, Col } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ownerIntelligenceService from '@/services/ownerIntelligence.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { OiPageFrame, OiEmptyState } from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { OiAreaTrendChart } from '@/components/owner-intelligence/charts/OiCharts';

const ScenarioManagerPage: React.FC = () => {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { formatCurrency } = useCurrency();
  const [name, setName] = useState('');
  const [salesVol, setSalesVol] = useState(100);
  const [marginAdj, setMarginAdj] = useState(0);
  const [collectionDelay, setCollectionDelay] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [compareResult, setCompareResult] = useState<{
    scenarios?: Array<{
      scenario_id: string;
      scenario_name: string;
      outputs?: { revenue_impact?: number; profit_impact?: number; cash_impact?: number; risk_level?: string };
    }>;
  } | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['owner-intelligence', 'scenarios'],
    queryFn: () => ownerIntelligenceService.listScenarios(),
  });

  const handleCreate = async () => {
    try {
      await ownerIntelligenceService.createScenario({
        scenario_name: name || 'New scenario',
        variables: [
          { key: 'sales_volume', value: salesVol, unit: 'percent' },
          { key: 'gross_margin', value: marginAdj, unit: 'percent' },
          { key: 'collection_delay', value: collectionDelay, unit: 'days' },
        ],
      });
      message.success('Scenario created', 4);
      qc.invalidateQueries({ queryKey: ['owner-intelligence', 'scenarios'] });
      setName('');
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const compare = async () => {
    if (selected.length < 2) {
      message.warning('Select at least 2 scenarios to compare');
      return;
    }
    try {
      const result = await ownerIntelligenceService.compareScenarios(selected);
      setCompareResult(result as typeof compareResult);
      message.success('Comparison ready', 4);
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const rows = (data as Array<Record<string, unknown>>) ?? [];

  const compareChart = (compareResult?.scenarios ?? []).map((s) => ({
    label: s.scenario_name,
    revenue: s.outputs?.revenue_impact ?? 0,
  }));

  return (
    <OiPageFrame subtitle="Scenario planning with sensitivity variables and impact comparison." loading={isLoading && !data}>
      <Card
        title="Scenario manager"
        bordered={false}
        extra={
          <Space direction="vertical" style={{ alignItems: 'flex-end' }}>
            <Space>
              <Input placeholder="Scenario name" value={name} onChange={(e) => setName(e.target.value)} />
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                Create
              </Button>
            </Space>
            <Row gutter={8}>
              <Col>
                <span className="oi-text-muted" style={{ fontSize: 11 }}>Sales vol %</span>
                <InputNumber min={50} max={150} value={salesVol} onChange={(v) => setSalesVol(Number(v ?? 100))} />
              </Col>
              <Col>
                <span className="oi-text-muted" style={{ fontSize: 11 }}>Margin ±pts</span>
                <InputNumber min={-20} max={20} value={marginAdj} onChange={(v) => setMarginAdj(Number(v ?? 0))} />
              </Col>
              <Col>
                <span className="oi-text-muted" style={{ fontSize: 11 }}>Collection delay (days)</span>
                <InputNumber min={0} max={90} value={collectionDelay} onChange={(v) => setCollectionDelay(Number(v ?? 0))} />
              </Col>
            </Row>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        {rows.length === 0 ? (
          <OiEmptyState
            title="No scenarios yet"
            body="Create your first what-if scenario to compare the impact of decisions."
            actionLabel="Create Scenario"
            onAction={handleCreate}
          />
        ) : (
          <>
            <Table
              size="small"
              rowKey="id"
              dataSource={rows}
              rowSelection={{
                selectedRowKeys: selected,
                onChange: (keys) => setSelected(keys as string[]),
              }}
              columns={[
                { title: 'Name', dataIndex: 'scenario_name' },
                { title: 'Status', dataIndex: 'status' },
                { title: 'Baseline', dataIndex: 'baseline', render: (v: boolean) => (v ? 'Yes' : 'No') },
                { title: 'Created', dataIndex: 'created_at' },
              ]}
            />
            <Button style={{ marginTop: 12 }} onClick={compare} disabled={selected.length < 2}>
              Compare selected
            </Button>
          </>
        )}
      </Card>

      {compareResult?.scenarios ? (
        <Card title="Scenario comparison — impact outputs" bordered={false}>
          <OiAreaTrendChart data={compareChart} dataKey="revenue" xKey="label" height={220} />
          <Table
            size="small"
            rowKey="scenario_id"
            style={{ marginTop: 16 }}
            dataSource={compareResult.scenarios}
            columns={[
              { title: 'Scenario', dataIndex: 'scenario_name' },
              { title: 'Revenue impact', dataIndex: ['outputs', 'revenue_impact'], render: (v: number) => formatCurrency(v) },
              { title: 'Profit impact', dataIndex: ['outputs', 'profit_impact'], render: (v: number) => formatCurrency(v) },
              { title: 'Cash impact', dataIndex: ['outputs', 'cash_impact'], render: (v: number) => formatCurrency(v) },
              { title: 'Risk', dataIndex: ['outputs', 'risk_level'] },
            ]}
          />
        </Card>
      ) : null}
    </OiPageFrame>
  );
};

export default ScenarioManagerPage;
